
import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';
import { CustomTooltip } from "./CustomTooltip";
import { formatNumber } from "../utils/chartUtils";
import { FinancialData } from "../types";

type FinancialChartProps = {
  data: number[];
  title: string;
  financialData: FinancialData | null;
};

export const FinancialChart = ({ data, title, financialData }: FinancialChartProps) => {
  const chartData = data.map((value, index) => ({
    date: financialData?.labels[index] || '',
    value
  }));

  const hasData = data.length > 0;

  const renderChart = () => {
    switch (title) {
      case 'Revenue':
        return (
          <BarChart data={chartData}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#4ade80" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="date" stroke="#22c55e" tick={{ fill: '#86efac', fontSize: 10 }} />
            <YAxis stroke="#22c55e" tickFormatter={formatNumber} tick={{ fill: '#bbf7d0', fontSize: 10 }} />
            {hasData && <Tooltip content={<CustomTooltip title={title} hasData={hasData} />} />}
            <Bar dataKey="value" fill="url(#revenueGradient)" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      case 'Gross Profit':
        return (
          <BarChart data={chartData}>
            <defs>
              <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#eab308" stopOpacity={0.8} />
                <stop offset="100%" stopColor="#fde047" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="date" stroke="#eab308" tick={{ fill: '#fef08a', fontSize: 10 }} />
            <YAxis stroke="#eab308" tickFormatter={formatNumber} tick={{ fill: '#fde047', fontSize: 10 }} />
            {hasData && <Tooltip content={<CustomTooltip title={title} hasData={hasData} />} />}
            <Bar dataKey="value" fill="url(#profitGradient)" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      case 'EPS':
        return (
          <LineChart data={chartData}>
            <defs>
              <linearGradient id="epsGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#38bdf8" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="date" stroke="#0ea5e9" tick={{ fill: '#7dd3fc', fontSize: 10 }} />
            <YAxis stroke="#0ea5e9" tickFormatter={formatNumber} tick={{ fill: '#38bdf8', fontSize: 10 }} />
            {hasData && <Tooltip content={<CustomTooltip title={title} hasData={hasData} />} />}
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="url(#epsGradient)" 
              strokeWidth={3} 
              dot={{ fill: '#38bdf8', stroke: '#0ea5e9', strokeWidth: 2, r: 4 }} 
              activeDot={hasData ? { r: 6, fill: '#0ea5e9' } : false} 
            />
          </LineChart>
        );
      case 'Free Cash Flow':
        return (
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="cashFlowGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f97316" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#fb923c" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="date" stroke="#f97316" tick={{ fill: '#fdba74', fontSize: 10 }} />
            <YAxis stroke="#f97316" tickFormatter={formatNumber} tick={{ fill: '#fb923c', fontSize: 10 }} />
            {hasData && <Tooltip content={<CustomTooltip title={title} hasData={hasData} />} />}
            <Area type="monotone" dataKey="value" stroke="#f97316" fill="url(#cashFlowGradient)" />
          </AreaChart>
        );
      case 'Total Debt':
        return (
          <BarChart data={chartData}>
            <defs>
              <linearGradient id="debtGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#dc2626" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="date" stroke="#dc2626" tick={{ fill: '#fca5a5', fontSize: 10 }} />
            <YAxis stroke="#dc2626" tickFormatter={formatNumber} tick={{ fill: '#ef4444', fontSize: 10 }} />
            {hasData && <Tooltip content={<CustomTooltip title={title} hasData={hasData} />} />}
            <Bar dataKey="value" fill="url(#debtGradient)" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      case 'EBITDA Margins':
        return (
          <LineChart data={chartData}>
            <defs>
              <linearGradient id="ebitdaGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#a78bfa" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="date" stroke="#8b5cf6" tick={{ fill: '#c4b5fd', fontSize: 10 }} />
            <YAxis stroke="#8b5cf6" tickFormatter={(value) => `${value.toFixed(1)}%`} tick={{ fill: '#ddd6fe', fontSize: 10 }} />
            {hasData && <Tooltip content={<CustomTooltip title={title} hasData={hasData} />} />}
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="url(#ebitdaGradient)" 
              strokeWidth={3} 
              dot={{ fill: '#a78bfa', stroke: '#8b5cf6', strokeWidth: 2, r: 4 }} 
              activeDot={hasData ? { r: 6, fill: '#8b5cf6' } : false} 
            />
          </LineChart>
        );
      default:
        return (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="date" stroke="#9b87f5" tick={{ fill: '#a5b4fc', fontSize: 10 }} />
            <YAxis stroke="#9b87f5" tickFormatter={formatNumber} tick={{ fill: '#818cf8', fontSize: 10 }} />
            {hasData && <Tooltip content={<CustomTooltip title={title} hasData={hasData} />} />}
            <Line type="monotone" dataKey="value" stroke="#9b87f5" />
          </LineChart>
        );
    }
  };

  return (
    <div className="h-40 w-full">
      <h4 className="text-[10px] font-semibold text-primary-light mb-2">{title}</h4>
      <ResponsiveContainer width="100%" height="100%">
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};
